"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.fieldConditionSingleDependencyToJsonSchema = fieldConditionSingleDependencyToJsonSchema;
exports.singleFieldConditionsToJsonSchema = singleFieldConditionsToJsonSchema;
exports.conditionsToJsonSchema = conditionsToJsonSchema;
exports.fieldsToJsonSchema = fieldsToJsonSchema;
function toJsonSchemaType(type) {
    switch (type) {
        case 'string':
        case 'text':
        case 'password':
            return 'string';
        case 'datetime':
            return ['string', 'number'];
        default:
            return type;
    }
}
const fieldKeyIsDotNotation = (fieldKey) => {
    return fieldKey.split('.').length > 1;
};
const generateThenStatement = (fieldKey) => {
    if (fieldKeyIsDotNotation(fieldKey)) {
        const [parentKey, childKey] = fieldKey.split('.');
        return {
            required: [parentKey],
            properties: {
                [parentKey]: {
                    required: [childKey]
                }
            }
        };
    }
    return {
        required: [fieldKey]
    };
};
const undefinedConditionValueToJSONSchema = (dependantFieldKey, fieldKey, operator, multiple) => {
    if (operator !== 'is' && operator !== 'is_not') {
        throw new Error(`Unsupported conditionally required field operator: ${operator}`);
    }
    const insideIfStatement = operator === 'is' ? { not: { required: [dependantFieldKey] } } : { required: [dependantFieldKey] };
    if (multiple) {
        return insideIfStatement;
    }
    if (operator === 'is') {
        const fieldIsNull = { properties: { [dependantFieldKey]: { type: 'null' } } };
        return {
            if: { anyOf: [insideIfStatement, fieldIsNull] },
            then: generateThenStatement(fieldKey)
        };
    }
    const fieldIsNotNull = { not: { properties: { [dependantFieldKey]: { type: 'null' } } } };
    return {
        if: { allOf: [insideIfStatement, fieldIsNotNull] },
        then: generateThenStatement(fieldKey)
    };
};
const simpleConditionToJSONSchema = (dependantFieldKey, fieldKey, dependantValue, operator, multiple) => {
    const dependantValueToJSONSchema = operator === 'is' ? { const: dependantValue } : { not: { const: dependantValue } };
    if (multiple) {
        return {
            properties: { [dependantFieldKey]: dependantValueToJSONSchema },
            required: [dependantFieldKey]
        };
    }
    return {
        if: {
            required: [dependantFieldKey],
            properties: { [dependantFieldKey]: dependantValueToJSONSchema }
        },
        then: generateThenStatement(fieldKey)
    };
};
const objectConditionToJSONSchema = (objectParentKey, objectChildKey, fieldKey, dependantValue, operator, multiple) => {
    const dependantValueToJSONSchema = operator === 'is' ? { const: dependantValue } : { not: { const: dependantValue } };
    if (multiple) {
        return {
            properties: {
                [objectParentKey]: { properties: { [objectChildKey]: dependantValueToJSONSchema }, required: [objectChildKey] }
            },
            required: [objectParentKey]
        };
    }
    return {
        if: {
            properties: {
                [objectParentKey]: { properties: { [objectChildKey]: dependantValueToJSONSchema }, required: [objectChildKey] }
            },
            required: [objectParentKey]
        },
        then: generateThenStatement(fieldKey)
    };
};
function fieldConditionSingleDependencyToJsonSchema(condition, fieldKey) {
    let jsonCondition = undefined;
    const innerCondition = condition;
    const dependentFieldKey = innerCondition.fieldKey;
    if (dependentFieldKey.split('.').length > 1) {
        const [parentKey, childKey] = dependentFieldKey.split('.');
        if (innerCondition.operator === 'is') {
            jsonCondition = objectConditionToJSONSchema(parentKey, childKey, fieldKey, innerCondition.value, 'is');
        }
        else if (innerCondition.operator === 'is_not') {
            jsonCondition = objectConditionToJSONSchema(parentKey, childKey, fieldKey, innerCondition.value, 'is_not');
        }
        else {
            throw new Error(`Unsupported conditionally required field operator: ${innerCondition.operator}`);
        }
        return jsonCondition;
    }
    if (innerCondition.operator === 'is') {
        if (innerCondition.value === undefined) {
            return undefinedConditionValueToJSONSchema(innerCondition.fieldKey, fieldKey, 'is');
        }
        jsonCondition = simpleConditionToJSONSchema(innerCondition.fieldKey, fieldKey, innerCondition.value, 'is');
    }
    else if (innerCondition.operator === 'is_not') {
        if (innerCondition.value === undefined) {
            return undefinedConditionValueToJSONSchema(innerCondition.fieldKey, fieldKey, 'is_not');
        }
        jsonCondition = simpleConditionToJSONSchema(innerCondition.fieldKey, fieldKey, innerCondition.value, 'is_not');
    }
    else {
        throw new Error(`Unsupported conditionally required field operator: ${innerCondition.operator}`);
    }
    return jsonCondition;
}
function singleFieldConditionsToJsonSchema(fieldKey, singleFieldConditions) {
    let jsonCondition = undefined;
    if (singleFieldConditions.conditions.length === 1) {
        return fieldConditionSingleDependencyToJsonSchema(singleFieldConditions.conditions[0], fieldKey);
    }
    const innerConditionArray = [];
    singleFieldConditions.conditions.forEach((innerCondition) => {
        const dependentFieldKey = innerCondition.fieldKey;
        if (dependentFieldKey.split('.').length > 1) {
            const [parentKey, childKey] = dependentFieldKey.split('.');
            const conditionToJSON = objectConditionToJSONSchema(parentKey, childKey, fieldKey, innerCondition.value, innerCondition.operator, true);
            innerConditionArray.push(conditionToJSON);
            const innerIfStatement = singleFieldConditions.match === 'any' ? { anyOf: innerConditionArray } : { allOf: innerConditionArray };
            jsonCondition = { if: innerIfStatement, then: generateThenStatement(fieldKey) };
            return jsonCondition;
        }
        if (innerCondition.value === undefined) {
            innerConditionArray.push(undefinedConditionValueToJSONSchema(innerCondition.fieldKey, fieldKey, innerCondition.operator, true));
        }
        else {
            const conditionToJSON = simpleConditionToJSONSchema(dependentFieldKey, fieldKey, innerCondition.value, innerCondition.operator, true);
            innerConditionArray.push(conditionToJSON);
        }
    });
    const innerIfStatement = singleFieldConditions.match === 'any' ? { anyOf: innerConditionArray } : { allOf: innerConditionArray };
    jsonCondition = { if: innerIfStatement, then: { required: [fieldKey] } };
    return jsonCondition;
}
function conditionsToJsonSchema(allFieldConditions) {
    const additionalSchema = [];
    for (const [fieldKey, singleFieldCondition] of Object.entries(allFieldConditions)) {
        const jsonCondition = singleFieldConditionsToJsonSchema(fieldKey, singleFieldCondition);
        if (jsonCondition === undefined) {
            throw new Error(`Unsupported conditionally required field condition: ${singleFieldCondition}`);
        }
        if (jsonCondition) {
            additionalSchema.push(jsonCondition);
        }
    }
    if (additionalSchema.length === 0) {
        return {};
    }
    return { allOf: additionalSchema };
}
function fieldsToJsonSchema(fields = {}, options, additionalSchema) {
    const required = [];
    const properties = {};
    const conditions = {};
    for (const [key, field] of Object.entries(fields)) {
        const schemaType = toJsonSchemaType(field.type);
        let schema = {
            title: field.label,
            description: field.description,
            type: schemaType,
            format: field.format,
            default: field.default
        };
        if (field.type === 'datetime') {
            schema.format = 'date-like';
            if (options?.tsType) {
                schema.tsType = 'string | number';
            }
        }
        else if (field.type === 'password') {
            schema.format = 'password';
        }
        else if (field.type === 'text') {
            schema.format = 'text';
        }
        else if (field.type === 'string') {
            const { minimum = null, maximum = null } = field;
            if (minimum) {
                schema.minLength = field?.minimum;
            }
            if (maximum) {
                schema.maxLength = field?.maximum;
            }
        }
        else if (field.type === 'number') {
            const { minimum = null, maximum = null } = field;
            if (minimum) {
                schema.minimum = field?.minimum;
            }
            if (maximum) {
                schema.maximum = field?.maximum;
            }
        }
        if (field.choices) {
            schema.enum = field.choices.map((choice) => {
                if (typeof choice === 'string') {
                    return choice;
                }
                return choice.value;
            });
        }
        if ('allowNull' in field && field.allowNull) {
            schema.type = [].concat(schemaType, 'null');
            if (schema.enum) {
                schema.enum = [...schema.enum, null];
            }
            if (typeof schema.tsType === 'string' && !schema.tsType.includes('null')) {
                schema.tsType += ' | null';
            }
        }
        const isMulti = 'multiple' in field && field.multiple;
        if (isMulti) {
            schema.items = { type: schemaType };
            schema.type = 'array';
            if (schema.enum) {
                schema.items.enum = schema.enum;
                delete schema.enum;
            }
        }
        if (schemaType === 'object' && field.properties) {
            const propertiesContainsConditionallyRequired = Object.values(field.properties).some((field) => field.required && typeof field.required === 'object');
            if (isMulti) {
                schema.items = fieldsToJsonSchema(field.properties, {
                    additionalProperties: field?.additionalProperties || false,
                    omitRequiredSchemas: propertiesContainsConditionallyRequired
                });
            }
            else {
                schema = {
                    ...schema,
                    ...fieldsToJsonSchema(field.properties, {
                        additionalProperties: field?.additionalProperties || false,
                        omitRequiredSchemas: propertiesContainsConditionallyRequired
                    })
                };
            }
            for (const [propertyKey, objectField] of Object.entries(field.properties)) {
                if (objectField.required === true) {
                    continue;
                }
                else if (objectField.required && typeof objectField.required === 'object') {
                    const dotNotationKey = `${key}.${propertyKey}`;
                    conditions[dotNotationKey] = objectField.required;
                }
            }
        }
        properties[key] = schema;
        if (field.required === true) {
            required.push(key);
        }
        else if (field.required && typeof field.required === 'object') {
            conditions[key] = field.required;
        }
    }
    if (options?.tsType === true) {
        return {
            $schema: 'http://json-schema.org/schema#',
            type: 'object',
            additionalProperties: options?.additionalProperties || false,
            properties,
            required
        };
    }
    if (options?.omitRequiredSchemas === true) {
        return {
            $schema: 'http://json-schema.org/schema#',
            type: 'object',
            additionalProperties: options?.additionalProperties || false,
            properties,
            ...additionalSchema
        };
    }
    return {
        $schema: 'http://json-schema.org/schema#',
        type: 'object',
        additionalProperties: options?.additionalProperties || false,
        properties,
        required,
        ...conditionsToJsonSchema(conditions),
        ...additionalSchema
    };
}
//# sourceMappingURL=fields-to-jsonschema.js.map